import React, { Component } from "react";
import { FetchCSVAction, DeleteCSVAction, ViewCSVFileAction } from "../../../Actions/Analysis";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import ArrowLeftSVG from "../../../Assets/svg/ArrowLeftSVG.svg";
import { EllipsisVertical, Eye, FileX, Trash2 } from "lucide-react";
import TableLoader from "../../Common/TableLoader";
import Tooltip from "../../Common/Tooltip";
import { getCached, setCached, clearCached } from "../../../Utils/fileCache";

const OTHER_CACHE_KEY = (page) => `other-files-p${page}`;

class RenderOtherFiles extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      currentPage: 1,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
      totalCount: 0,
      itemsPerPage: null,
      openActionMenuId: null,
      pageInput: "1",
      loading: true,
    };
    this.actionMenuRef = React.createRef();
  }

  static propTypes = {
    FetchCSVAction: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.fetchFiles(1);
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (e) => {
    if (
      this.state.openActionMenuId !== null &&
      this.actionMenuRef.current &&
      !this.actionMenuRef.current.contains(e.target)
    ) {
      this.setState({ openActionMenuId: null });
    }
  };

  applyResponse = (response, page) => {
    const resultsCount = response.results ? response.results.length : 0;
    const totalCount = response.count || 0;
    let itemsPerPage;
    if (page === 1 || !this.state.itemsPerPage) {
      itemsPerPage = resultsCount > 0 ? resultsCount : 20;
    } else {
      itemsPerPage = this.state.itemsPerPage;
    }
    const baseItemsPerPage = page === 1 ? itemsPerPage : this.state.itemsPerPage || itemsPerPage;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / baseItemsPerPage) : 1;
    this.setState({
      files: response.results || [],
      currentPage: page,
      totalPages,
      totalCount,
      itemsPerPage: page === 1 ? itemsPerPage : this.state.itemsPerPage,
      hasNext: response.next !== null,
      hasPrevious: response.previous !== null,
      loading: false,
    });
  };

  fetchFiles = async (page) => {
    const cacheKey = OTHER_CACHE_KEY(page);
    const cached = getCached(cacheKey);
    if (cached) {
      this.applyResponse(cached, page);
      // Background refresh
      this.props.FetchCSVAction(page).then((response) => {
        if (response) {
          setCached(cacheKey, response);
          this.applyResponse(response, page);
        }
      }).catch(() => {});
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await this.props.FetchCSVAction(page);
      if (response) {
        setCached(cacheKey, response);
        this.applyResponse(response, page);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      this.setState({ loading: false });
    }
  };

  truncateFileName = (fileName, maxLength = 20) => {
    if (!fileName || fileName.length <= maxLength) return fileName || "No file name";
    const extensionIndex = fileName.lastIndexOf(".");
    const extension = extensionIndex !== -1 ? fileName.slice(extensionIndex) : "";
    const nameWithoutExt = extensionIndex !== -1 ? fileName.slice(0, extensionIndex) : fileName;
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);
    return `${truncatedName}...${extension}`;
  };

  toggleActionMenu = (fileId) => {
    this.setState((prevState) => ({
      openActionMenuId: prevState.openActionMenuId === fileId ? null : fileId,
    }));
  };

  fetchFilesSilent = async (page) => {
    const cacheKey = OTHER_CACHE_KEY(page);
    try {
      const response = await this.props.FetchCSVAction(page);
      if (response) {
        setCached(cacheKey, response);
        this.applyResponse(response, page);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  handleActionClick = (action, file) => {
    this.setState({ openActionMenuId: null });
    if (action === "View") {
      this.props.ViewCSVFileAction(file.id);
    } else if (action === "Delete") {
      // Optimistically remove the file from local state immediately
      this.setState((prev) => ({ files: prev.files.filter((f) => f.id !== file.id) }));
      this.props.DeleteCSVAction(file.id).then(() => {
        clearCached("other-files");
        this.fetchFilesSilent(this.state.currentPage);
      }).catch((err) => {
        console.error("Delete failed:", err);
        // Restore the file on failure
        this.fetchFiles(this.state.currentPage);
      });
    }
  };

  handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= this.state.totalPages) {
      this.setState({ currentPage: newPage, pageInput: String(newPage) }, () => {
        this.fetchFiles(newPage);
      });
    }
  };

  handlePageInputChange = (e) => {
    this.setState({ pageInput: e.target.value });
  };

  handlePageInputSubmit = (e) => {
    if (e.key === "Enter") {
      const page = parseInt(this.state.pageInput, 10);
      if (!isNaN(page) && page >= 1 && page <= this.state.totalPages) {
        this.handlePageChange(page);
      } else {
        this.setState({ pageInput: String(this.state.currentPage) });
      }
      e.target.blur();
    }
  };

  handlePageInputBlur = () => {
    const page = parseInt(this.state.pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= this.state.totalPages) {
      this.handlePageChange(page);
    } else {
      this.setState({ pageInput: String(this.state.currentPage) });
    }
  };

  renderPagination = () => {
    const { currentPage, totalPages } = this.state;

    return (
      <div className="flex justify-between items-center mt-2 pt-4">
        {/* Previous Button */}
        <button
          onClick={() => this.handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <img className="w-4 h-4" src={ArrowLeftSVG} alt="Previous" />
          Previous
        </button>

        {/* Page Input */}
        <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
          <span>Page</span>
          <input
            type="text"
            value={this.state.pageInput ?? currentPage}
            onChange={this.handlePageInputChange}
            onKeyDown={this.handlePageInputSubmit}
            onBlur={this.handlePageInputBlur}
            onFocus={(e) => e.target.select()}
            className="w-12 h-9 text-center border border-gray-200 rounded-lg text-sm font-medium text-gray-900 outline-none focus:border-primary-blue transition-colors"
          />
          <span>of {totalPages}</span>
        </div>

        {/* Next Button */}
        <button
          onClick={() => this.handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <img className="rotate-180 w-4 h-4" src={ArrowLeftSVG} alt="Next" />
        </button>
      </div>
    );
  };

  render() {
    const { openActionMenuId, files, loading } = this.state;

    return (
      <div className="flex flex-col h-full">
        {/* Table */}
        <div className="flex-1 bg-white border border-blue-100 rounded-xl flex flex-col">
          {/* Title bar — aligned with UploadFiles (Knowledge Base) */}
          <div className="px-6 py-4 border-b border-blue-100 rounded-t-xl" style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}>
            <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
              Files ({this.state.totalCount ?? 0})
            </p>
          </div>
          {/* Column headers */}
          <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr] items-center py-2 bg-gray-50/50 px-5 gap-4 border-b border-gray-100">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">File Name</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Date Uploaded</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">File Size</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Status</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center">Actions</div>
          </div>

          {/* File Rows */}
          {loading ? (
            <TableLoader />
          ) : files.length > 0 ? (
            files.map((file) => (
              <div
                key={file.id}
                className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr] py-3.5 px-5 gap-4 border-t border-gray-100 font-inter text-sm text-gray-700 hover:bg-blue-50/40 transition-colors"
              >
                <div className="font-medium text-gray-900 overflow-visible">
                  <Tooltip text={file.file_name || "No file name"} icon={false}>
                    <span className="cursor-default">{this.truncateFileName(file.file_name)}</span>
                  </Tooltip>
                </div>
                <div className="text-gray-500 truncate">
                  {new Date(file.uploaded_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <div className="text-gray-500">{file.file_size}</div>
                <div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      file.processing_status?.toLowerCase() === "completed"
                        ? "bg-green-50 text-green-700"
                        : file.processing_status?.toLowerCase() === "failed"
                        ? "bg-red-50 text-red-700"
                        : "bg-yellow-50 text-yellow-700"
                    }`}
                  >
                    {file.processing_status}
                  </span>
                </div>
                <div ref={openActionMenuId === file.id ? this.actionMenuRef : null} className="relative flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => this.toggleActionMenu(file.id)}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                    aria-label="More actions"
                  >
                    <EllipsisVertical className="w-4 h-4" />
                  </button>
                  {openActionMenuId === file.id && (
                    <div className="absolute right-0 top-8 z-20 w-32 bg-white border border-gray-100 rounded-lg shadow-custom-shadow py-1 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => this.handleActionClick("View", file)}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-primary-blue transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => this.handleActionClick("Delete", file)}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-1 w-full flex-col items-center justify-center gap-6 py-12">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)" }}>
                <FileX className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-600">
                  No files uploaded yet
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Upload a CSV or data file to get started
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pagination — always at bottom */}
        <div className="mt-auto pt-4 pb-6">
          {this.renderPagination()}
        </div>
      </div>
    );
  }
}

export default withRouterHooks(
  connect(null, { FetchCSVAction, DeleteCSVAction, ViewCSVFileAction })(RenderOtherFiles)
);
